import type { AuthenticationProps, SignInData } from '@iheartjane/menu';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useCheckJaneUserExists } from '@jane/bloom/hooks/account/useCheckJaneUserExists';
import { useLogin } from '@jane/bloom/hooks/account/useLogin';
import { useSignup } from '@jane/bloom/hooks/account/useSignup';
import { useResetPasswordRequestFetcher } from '@jane/bloom/hooks/useResetPasswordRequestFetcher';
import { formatLabel } from '@jane/bloom/utils/strings/formatLabel';

const EMAIL_CHECK_FAILED_MESSAGE =
  'We could not check that email. Please try again.';
const GENERAL_ERROR_KEY = 'message';

const formatFieldErrors = (
  errs: Record<string, string> | undefined
): string[] =>
  errs
    ? Object.entries(errs)
        .filter(([, message]) => !!message)
        .map(([field, message]) =>
          // Some errors are like { message: 'This password is incorrect' }
          // while some are like { password: 'is insecure...' }
          field === GENERAL_ERROR_KEY
            ? message
            : `${formatLabel(field)} ${message}`
        )
    : [];

type AuthenticationErrors = NonNullable<AuthenticationProps['errors']>;
type EmailFormData = Parameters<
  AuthenticationProps['onForgotPasswordSubmit']
>[0];
type SignUpFormData = Parameters<AuthenticationProps['onSignUpSubmit']>[0];

export interface BloomAuthSuccess {
  redirectTo: string;
}

interface UseBloomAuthHandlersOptions {
  onAuthSuccess: (result: BloomAuthSuccess) => void;
}

export interface UseBloomAuthHandlersResult {
  errors: AuthenticationErrors | null;
  onCheckUserExists: (email: string) => Promise<boolean>;
  onForgotPasswordSubmit: (data: EmailFormData) => Promise<boolean>;
  onSignInSubmit: (data: SignInData) => void;
  onSignUpSubmit: (data: SignUpFormData) => void;
}

/**
 * Wires the four auth API endpoints to the handler shape expected by
 * `<Authentication>` or `<LoginModal>` from `@iheartjane/menu`.
 *
 * - `onCheckUserExists` and `onForgotPasswordSubmit` must return promises
 *   so the Authentication/LoginModal component can wait for the result and render the next screen.
 * - `onSignInSubmit` and `onSignUpSubmit` don't require promises, they just call
 *   onAuthSuccess when the submission succeeds
 */
export const useBloomAuthHandlers = ({
  onAuthSuccess,
}: UseBloomAuthHandlersOptions): UseBloomAuthHandlersResult => {
  const { fetcher: loginFetcher, login } = useLogin();
  const { checkUserExists, fetcher: checkFetcher } = useCheckJaneUserExists({
    skipRedirect: true,
  });
  const { fetcher: signupFetcher, signup } = useSignup();
  const {
    data: resetData,
    sendResetPasswordRequest,
    state: resetState,
  } = useResetPasswordRequestFetcher({ skipRedirect: true });

  const checkPending = useRef<{
    reject: (err: Error) => void;
    resolve: (exists: boolean) => void;
  } | null>(null);
  const resetPending = useRef<{
    resolve: (success: boolean) => void;
  } | null>(null);

  // Handle user existence check
  useEffect(() => {
    if (checkFetcher.state !== 'idle' || !checkPending.current) return;
    const { reject, resolve } = checkPending.current;
    checkPending.current = null;
    const data = checkFetcher.data as { isNewUser?: unknown } | undefined;
    if (data && typeof data.isNewUser === 'boolean') {
      resolve(!data.isNewUser);
    } else {
      reject(new Error(EMAIL_CHECK_FAILED_MESSAGE));
    }
  }, [checkFetcher.state, checkFetcher.data]);

  // Handle password reset request
  useEffect(() => {
    if (resetState !== 'idle' || !resetPending.current) return;
    const { resolve } = resetPending.current;
    resetPending.current = null;
    resolve(resetData?.success === true);
  }, [resetState, resetData]);

  // `onAuthSuccess` must be memoized by the caller (e.g. via useCallback) — a
  // new identity on every render would re-fire these effects because the
  // fetcher's `data?.success` persists after a successful submission.
  useEffect(() => {
    if (loginFetcher.state === 'idle' && loginFetcher.data?.success) {
      onAuthSuccess({ redirectTo: loginFetcher.data.redirectTo });
    }
  }, [loginFetcher.state, loginFetcher.data, onAuthSuccess]);

  useEffect(() => {
    if (signupFetcher.state === 'idle' && signupFetcher.data?.success) {
      onAuthSuccess({ redirectTo: signupFetcher.data.redirectTo });
    }
  }, [signupFetcher.state, signupFetcher.data, onAuthSuccess]);

  const onCheckUserExists = useCallback(
    (email: string) =>
      new Promise<boolean>((resolve, reject) => {
        checkPending.current = { reject, resolve };
        checkUserExists(email);
      }),
    [checkUserExists]
  );

  const onSignInSubmit = useCallback(
    ({ email, password }: SignInData) => {
      login({ email, password });
    },
    [login]
  );

  const onSignUpSubmit = useCallback(
    ({ email, password, phone }: SignUpFormData) => {
      signup({ email, password, phoneNumber: phone });
    },
    [signup]
  );

  const onForgotPasswordSubmit = useCallback(
    ({ email }: EmailFormData) =>
      new Promise<boolean>((resolve) => {
        resetPending.current = { resolve };
        sendResetPasswordRequest(email);
      }),
    [sendResetPasswordRequest]
  );

  const loginErrors =
    loginFetcher.state === 'idle' &&
    loginFetcher.data &&
    !loginFetcher.data.success
      ? loginFetcher.data.errors
      : undefined;
  const signupErrors =
    signupFetcher.state === 'idle' &&
    signupFetcher.data &&
    !signupFetcher.data.success
      ? signupFetcher.data.errors
      : undefined;

  const errors = useMemo<AuthenticationErrors | null>(() => {
    const signIn = formatFieldErrors(loginErrors);
    const signUp = formatFieldErrors(signupErrors);
    if (signIn.length === 0 && signUp.length === 0) return null;
    return {
      signIn: signIn.length > 0 ? signIn : undefined,
      signUp: signUp.length > 0 ? signUp : undefined,
    };
  }, [loginErrors, signupErrors]);

  return {
    errors,
    onCheckUserExists,
    onForgotPasswordSubmit,
    onSignInSubmit,
    onSignUpSubmit,
  };
};
